<template>
  <div class="tw-document-preview">
    <!-- Template sélectionné : affichage du PDF -->
    <div v-if="templateSelected" class="card-body" style="display: grid; grid-template-rows: auto 1fr; height: 100%;">
      <div class="bg-light row d-flex justify-content-between align-items-center w-100 p-3"
        style="top: 0">
        <div>
          <q-btn v-if="previewImages.length > 0" class="btn btn-outline-primary" icon="download" :loading="isDownloading"
            @click="onDownloadEvent">
            <template v-slot:loading>
              <q-spinner-facebook />
            </template>
            {{ $t("task.buttons.download_document") }}
          </q-btn>
        </div>
        <div>
          <div class="d-flex align-items-center justify-content-between" style="gap: 10px">
            <div>
              <q-btn class="btn btn-outline-primary" icon="arrow_back_ios" @click="_previousPage" />
            </div>
            <div>
              <div class="flex flex-center">
                <q-item-label>
                  {{ this.currentPreviewIndex + 1 }}
                </q-item-label>
              </div>
            </div>
            <div>
              <q-btn class="btn btn-outline-primary" icon="arrow_forward_ios" @click="_nextPage" />
            </div>
          </div>
        </div>
      </div>

      <div style="overflow: hidden;"><img v-if="previewImages.length > 0" :src="previewImages[currentPreviewIndex]" style="width: 100%; height: 100%;" /></div>
    </div>
    <!-- Aucun template sélectionné -->
    <div v-if="!templateSelected" class="card-body d-flex justify-content-center align-items-center">
      <span class="material-icons tw-document-icon">description</span>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { VuePDF } from "@tato30/vue-pdf";
import store from "@/store";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
@Options({
  props: ["reference"],
  components: {
    VuePDF,
  },
})
export default class PrintDocumentPreview extends Vue {
  // Props
  reference: any;

  // Données réactives
  currentPreviewIndex = 0; // Index de la page affichée
  previewImages: string[] = []; // URLs des pages du PDF
  dynamicHeight = 630;      // Hauteur max par défaut
  isDownloading = false;    // État du téléchargement
  loading = false;          // Chargement du PDF
  pdfError: Error | null = null; // Erreur éventuelle

  // Getters simplifiés
  get templateSelected() {
    return store.state.printDocumentModule.previewTemplate;
  }

  get visualizedTemplate() {
    return store.state.printDocumentModule.visualizedTemplate;
  }

  get fileId() {
    return store.state.printDocumentModule.fileDetailFileId;
  }
  _nextPage() {
      this.currentPreviewIndex = (this.currentPreviewIndex + 1) % this.previewImages.length;
  }

  _previousPage() {
      this.currentPreviewIndex = (this.currentPreviewIndex - 1 + this.previewImages.length) % this.previewImages.length;
  }

  async getPageBlobUrls(blob: Uint8Array | null | undefined): Promise<string[]> {
  if (!blob) return [];
  const pdf = await pdfjsLib.getDocument({ data: blob }).promise;

  const urls = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);

    const viewport = page.getViewport({ scale: 2 });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) throw new Error("Failed to get canvas context");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: ctx,
      viewport
    }).promise;

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );

    const url = URL.createObjectURL(blob as any);
    urls.push(url);
  }

  return urls;
}
  // Watcher sur le store
  watchStorePreview() {
    // On surveille les changements de previewTemplate
    this.$watch(
      () => store.state.printDocumentModule.previewTemplate,
      async (newVal:any) => {
        if (newVal && newVal.arr) {
          this.loading = true;
          this.previewImages = await this.getPageBlobUrls(newVal.arr);
          this.currentPreviewIndex = 0;
          this.loading = false;
        }
      },
      { immediate: true } // S'exécute immédiatement à la création du composant
    );
  }

  // Normalise les données du PDF (base64 -> Uint8Array)
  normalizePdfData(data: any): Uint8Array | ArrayBuffer {
    // Si c'est déjà un Uint8Array ou ArrayBuffer, on le retourne
    if (data instanceof Uint8Array || data instanceof ArrayBuffer) {
      return data;
    }
    // Si c'est une chaîne base64
    if (typeof data === "string") {
      const binaryString = atob(data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    }
    // Si c'est un tableau de bytes
    if (Array.isArray(data)) {
      return new Uint8Array(data);
    }
    throw new Error("Format de données PDF non supporté");
  }


  // Téléchargement
  onDownloadEvent() {
    store.dispatch("printDocumentModule/downloadFile", {
      request: {
        resourceUid: this.fileId,
      },
      callback: this.downloadDocumentResponse,
    });
  }

  downloadDocumentResponse(response: any) {
    if (response) {
      const { content } = response;
      const fileName = this.visualizedTemplate?.fileName || response?.originalFileName;
      const linkSource = `data:${response.format};base64,${content}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = `${fileName}_${this.reference}.pdf`;
      downloadLink.click();
      this.viewDocumentResponse();
    }
  }

  viewDocumentResponse() {
    this.isDownloading = false;
  }

  // Gestion de la hauteur dynamique (zoom)
  adjustHeight() {
    const zoomLevel = window.devicePixelRatio || 1;
    const baseHeight = 630;
    this.dynamicHeight = baseHeight / zoomLevel;
  }

  // Lifecycle hooks
  mounted() {
    this.adjustHeight();
    window.addEventListener("resize", this.adjustHeight);
    this.watchStorePreview(); // Initialise le watcher
  }

  beforeUnmount() {
    window.removeEventListener("resize", this.adjustHeight);
  }
}
</script>

<style scoped>
.scrollable-content {
  overflow-y: auto;
}
</style>